import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { useUserInfo } from '@/api/use-user-info';
import { env } from '@/config/env';
import { reactQueryKeys } from '@/config/react-query';
import { useCodWeb } from '@/hooks/use-codweb';
import { fetchQueueRequest } from '@/lib/fetch-queue-request';
import { getApiHeaders } from '@/lib/getApiHeaders';
import {
  HttpError,
  queueRetryDelay,
  shouldRetryQueueRequest,
} from '@/lib/http-error';

type getExtensionParams = { codWeb: string; email: string };

const ExtensionSchema = z.object({
  number: z.string().min(1),
  password: z.string().min(1),
});

const GetExtensionSchema = z.object({
  extension: ExtensionSchema,
});

export const getExtension = async (params: getExtensionParams) => {
  const route = `${env.VITE_BACKEND_URL}/api/${params.codWeb}/users/${encodeURIComponent(params.email)}/extension`;
  const headers = await getApiHeaders();

  return fetchQueueRequest(route, { headers }, async (response) => {
    if (!response.ok) throw await HttpError.fromResponse(response);
    const { extension } = GetExtensionSchema.parse(await response.json());
    return extension;
  });
};

export const useExtension = () => {
  const codWeb = useCodWeb();
  const { data: userInfo } = useUserInfo();

  const email = userInfo?.email || '';

  return useQuery({
    queryKey: [reactQueryKeys.extension, String(codWeb), email],
    queryFn: () => getExtension({ codWeb, email }),
    enabled: Boolean(codWeb && email),
    // Missing or incomplete extensions need an administrator, not another request.
    retry: shouldRetryQueueRequest,
    retryDelay: queueRetryDelay,
    staleTime: Infinity,
  });
};
